import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { BREAKPOINT } from '@constants';
import { SliceFlexibleGridItem } from '@contracts';
import { getCommaSeparatedValues } from '@utils/helpers';
import Colors from '../Colors';
import LazyLoadImage from '../LazyLoadImage';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface FlexibleGridItemProps {
  item: SliceFlexibleGridItem;
  index: number;
  includeOverlay?: boolean;
  onImageLoad?: () => void;
}

const FlexibleGridItem: React.FC<FlexibleGridItemProps> = ({ item, index, includeOverlay = false, onImageLoad }) => {
  const desktop = useDesktop(768);
  const image = !desktop && item?.image_mobile?.url ? item.image_mobile.url : item?.image?.url;
  const pillElements = getCommaSeparatedValues(item?.pill_elements);

  return (
    <GridItem>
      <ImageWrapper>
        <LazyLoadImageStyled src={image ?? ''} alt={item?.image?.alt ?? ''} loading={index === 0 ? 'eager' : 'lazy'} afterLoad={onImageLoad} />
        {includeOverlay && <ImageOverlay data-testid="image-overlay" />}
      </ImageWrapper>
      <GridItemBody>
        <TextWrapper>
          <Heading $color={item.textcolor}>
            <PrismicRichText field={item.heading.richText} />
          </Heading>
          {item?.bodycopy.text && (
            <Typography $variant={TextVariant.BodyRegularLG} $color={item.textcolor}>
              {item.bodycopy.text}
            </Typography>
          )}
          {pillElements?.length > 0 && (
            <PillsGrid>
              {pillElements.map((pill, pillIndex) => (
                <Pill key={pillIndex}>
                  <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularSM} $color={item.textcolor}>
                    {pill}
                  </Typography>
                </Pill>
              ))}
            </PillsGrid>
          )}
        </TextWrapper>
        {item?.buttontext && (
          <LinkButton
            size={desktop ? 'normal' : 'small-round'}
            backgroundColor={Colors.cream}
            textColor={Colors.navyBlue}
            to={item.button_url}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {item.buttontext}
          </LinkButton>
        )}
      </GridItemBody>
    </GridItem>
  );
};

export default FlexibleGridItem;

const GridItem = styled.div`
  background: ${Colors.sage50};
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  text-align: left;
  border-radius: 12px;
  overflow: hidden;
  height: 500px;
  position: relative;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    height: 400px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding: 16px;
    height: 320px;
    margin-right: 8px;
    width: 350px;
    max-width: 80%;
  }

  @media (max-width: 340px) {
    max-width: 92%;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.6) 98.5%);
  pointer-events: none;
`;

const LazyLoadImageStyled = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const GridItemBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 24px;
  position: relative;
  z-index: 2;

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    row-gap: 8px;
  }

  & > a:hover {
    background: ${Colors.navyBlue};
    color: ${Colors.cream};
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Heading = styled.div<{ $color: string }>`
  > * {
    color: ${(props) => props.$color || Colors.navyBlue};
    ${TEXT_STYLES[TextVariant.Heading6]};

    @media (max-width: ${BREAKPOINT.mobileL}px) {
      ${TEXT_STYLES[TextVariant.BodyBoldLG]};
    }
  }
`;

const PillsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-items: start;
  gap: 8px;
`;

const Pill = styled.div`
  padding: 4px 6px;
  align-items: center;
  border-radius: 20px;
  background: rgba(255, 251, 231, 0.4);
  backdrop-filter: blur(5px);
`;
